import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { API_BASE_URL, apiService } from '../services/api';

export type ChatConnectionStatus = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

export interface ChatSocketEvent {
  id: number;
  data: string;
}

type UnreadMap = Record<string, number>;

interface ChatSocketPayload {
  type?: unknown;
  chatId?: unknown;
  senderId?: unknown;
  recipientId?: unknown;
}

interface ChatSocketContextType {
  status: ChatConnectionStatus;
  error: string | null;
  lastEvent: ChatSocketEvent | null;
  unreadByChat: UnreadMap;
  totalUnreadCount: number;
  markChatAsReadLocally: (chatId: string) => void;
  sendJson: (payload: unknown) => boolean;
}

const ChatSocketContext = createContext<ChatSocketContextType | undefined>(undefined);

const HEARTBEAT_INTERVAL_MS = 40000;
const RECONNECT_BASE_DELAY_MS = 2000;
const RECONNECT_MAX_DELAY_MS = 30000;

const getUnreadCountForUser = (value: unknown, currentUserId: string): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const unreadValue = (value as Record<string, unknown>)[currentUserId];
    if (typeof unreadValue === 'number' && Number.isFinite(unreadValue)) {
      return unreadValue;
    }
    if (typeof unreadValue === 'string') {
      const parsed = Number(unreadValue);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return 0;
};

const buildUnreadMap = (chats: unknown[], currentUserId: string): UnreadMap => {
  return chats.reduce<UnreadMap>((accumulator, chat) => {
    if (!chat || typeof chat !== 'object') {
      return accumulator;
    }

    const chatRecord = chat as Record<string, unknown>;
    const chatId =
      typeof chatRecord._id === 'string'
        ? chatRecord._id
        : typeof chatRecord.id === 'string'
          ? chatRecord.id
          : '';

    if (!chatId) {
      return accumulator;
    }

    accumulator[chatId] = getUnreadCountForUser(chatRecord.unreadCount, currentUserId);
    return accumulator;
  }, {});
};

const parseSocketPayload = (rawData: string): ChatSocketPayload | null => {
  try {
    const payload = JSON.parse(rawData) as ChatSocketPayload;
    return payload && typeof payload === 'object' ? payload : null;
  } catch {
    return null;
  }
};

const buildChatWsUrl = (token?: string | null) => {
  let baseUrl = '';

  try {
    const url = new URL(API_BASE_URL, window.location.origin);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    url.pathname = '/ws/chat';
    url.search = '';
    baseUrl = url.toString();
  } catch {
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const hostname = window.location.hostname || 'localhost';
    const port = window.location.port ? `${window.location.port}` : '80';
    baseUrl = `${protocol}://${hostname}:${port}/ws/chat`;
  }

  if (!token) return baseUrl;
  const separator = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
};

export const ChatSocketProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  const [status, setStatus] = useState<ChatConnectionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [lastEvent, setLastEvent] = useState<ChatSocketEvent | null>(null);
  const [unreadByChat, setUnreadByChat] = useState<UnreadMap>({});

  const socketRef = useRef<WebSocket | null>(null);
  const eventCounterRef = useRef(0);
  const heartbeatTimerRef = useRef<number | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectAllowedRef = useRef(false);

  const clearHeartbeatTimer = () => {
    if (heartbeatTimerRef.current !== null) {
      window.clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  };

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current !== null) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const closeActiveSocket = () => {
    if (!socketRef.current) return;
    const socket = socketRef.current;
    socketRef.current = null;
    if (
      socket.readyState === WebSocket.OPEN ||
      socket.readyState === WebSocket.CONNECTING
    ) {
      socket.close();
    }
  };

  useEffect(() => {
    if (isLoading) return;

    if (!isAuthenticated) {
      reconnectAllowedRef.current = false;
      clearReconnectTimer();
      clearHeartbeatTimer();
      reconnectAttemptsRef.current = 0;
      closeActiveSocket();
      setStatus('idle');
      setError(null);
      setUnreadByChat({});
      return;
    }

    reconnectAllowedRef.current = true;

    const sendHeartbeat = () => {
      if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
        return;
      }
      const currentUserId = user?.id;
      if (!currentUserId) return;

      socketRef.current.send(
        JSON.stringify({
          type: 'KEEPALIVE',
          senderId: currentUserId,
          recipientId: currentUserId,
          content: 'ping',
        })
      );
    };

    const scheduleReconnect = () => {
      if (!reconnectAllowedRef.current || reconnectTimerRef.current !== null) return;

      const attempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempt;

      const exponentialDelay = Math.min(
        RECONNECT_MAX_DELAY_MS,
        RECONNECT_BASE_DELAY_MS * 2 ** (attempt - 1)
      );
      const jitter = Math.floor(Math.random() * Math.min(1000, exponentialDelay * 0.4));
      const delay = exponentialDelay + jitter;

      setStatus('connecting');
      setError(`Chat disconnected. Reconnecting in ${Math.ceil(delay / 1000)}s...`);

      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        connect();
      }, delay);
    };

    const connect = () => {
      if (!reconnectAllowedRef.current) return;

      clearReconnectTimer();
      clearHeartbeatTimer();
      closeActiveSocket();

      const token = apiService.getToken();
      if (!token) {
        setStatus('error');
        setError('Authentication token missing. Please login again.');
        reconnectAllowedRef.current = false;
        return;
      }

      const wsUrl = buildChatWsUrl(token);
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;

      setStatus('connecting');

      socket.onopen = () => {
        if (socketRef.current !== socket) return;
        reconnectAttemptsRef.current = 0;
        setStatus('connected');
        setError(null);

        clearHeartbeatTimer();
        heartbeatTimerRef.current = window.setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
      };

      socket.onmessage = (event) => {
        if (socketRef.current !== socket || typeof event.data !== 'string') return;
        eventCounterRef.current += 1;
        setLastEvent({ id: eventCounterRef.current, data: event.data });
      };

      socket.onerror = () => {
        if (socketRef.current !== socket) return;
        setStatus('error');
        setError('Chat connection error. Attempting to reconnect...');
      };

      socket.onclose = (event) => {
        if (socketRef.current === socket) {
          socketRef.current = null;
        }

        clearHeartbeatTimer();

        if (!reconnectAllowedRef.current) {
          setStatus('disconnected');
          if (!event.wasClean) {
            setError('Chat connection closed unexpectedly.');
          }
          return;
        }

        setStatus('disconnected');
        if (!event.wasClean) {
          setError('Chat connection closed unexpectedly. Retrying...');
        }
        scheduleReconnect();
      };
    };

    setError(null);
    connect();

    return () => {
      reconnectAllowedRef.current = false;
      clearReconnectTimer();
      clearHeartbeatTimer();
      closeActiveSocket();
    };
  }, [user?.id, isAuthenticated, isLoading]);

  useEffect(() => {
    const currentUserId = user?.id;
    if (!currentUserId) {
      setUnreadByChat({});
      return;
    }

    let isMounted = true;

    const loadUnreadCounts = async () => {
      const pageSize = 50;
      const maxPages = 20;
      let offset = 0;
      let pagesFetched = 0;
      const allChats: unknown[] = [];

      while (pagesFetched < maxPages) {
        const page = await apiService.getChatsForUser(currentUserId, pageSize, offset);
        if (!Array.isArray(page) || page.length === 0) {
          break;
        }

        allChats.push(...page);
        if (page.length < pageSize) {
          break;
        }

        offset += pageSize;
        pagesFetched += 1;
      }

      if (!isMounted) {
        return;
      }

      setUnreadByChat(buildUnreadMap(allChats, currentUserId));
    };

    loadUnreadCounts().catch((loadError) => {
      console.error('Failed to load unread messages count:', loadError);
    });

    return () => {
      isMounted = false;
    };
  }, [user?.id, status]);

  useEffect(() => {
    const currentUserId = user?.id;
    if (!currentUserId || !lastEvent) {
      return;
    }

    const payload = parseSocketPayload(lastEvent.data);
    if (!payload) {
      return;
    }

    const type = typeof payload.type === 'string' ? payload.type : '';
    const chatId = typeof payload.chatId === 'string' ? payload.chatId : '';
    const senderId = typeof payload.senderId === 'string' ? payload.senderId : '';
    const recipientId = typeof payload.recipientId === 'string' ? payload.recipientId : '';

    if (!chatId) {
      return;
    }

    if (type === 'SEND_MESSAGE' && senderId !== currentUserId && recipientId === currentUserId) {
      setUnreadByChat((prev) => ({
        ...prev,
        [chatId]: (prev[chatId] ?? 0) + 1,
      }));
      return;
    }

    if (type === 'MARK_AS_READ' && senderId === currentUserId) {
      setUnreadByChat((prev) => ({
        ...prev,
        [chatId]: 0,
      }));
    }
  }, [user?.id, lastEvent]);

  const markChatAsReadLocally = (chatId: string) => {
    if (!chatId) {
      return;
    }

    setUnreadByChat((prev) => ({
      ...prev,
      [chatId]: 0,
    }));
  };

  const sendJson = (payload: unknown): boolean => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      setError('Chat connection is not ready.');
      return false;
    }

    socketRef.current.send(JSON.stringify(payload));
    return true;
  };

  const totalUnreadCount = useMemo(
    () => Object.values(unreadByChat).reduce((total, count) => total + count, 0),
    [unreadByChat]
  );

  const value = useMemo(
    () => ({ status, error, lastEvent, unreadByChat, totalUnreadCount, markChatAsReadLocally, sendJson }),
    [status, error, lastEvent, unreadByChat, totalUnreadCount]
  );

  return <ChatSocketContext.Provider value={value}>{children}</ChatSocketContext.Provider>;
};

export const useChatSocket = (): ChatSocketContextType => {
  const context = useContext(ChatSocketContext);
  if (!context) {
    throw new Error('useChatSocket must be used within a ChatSocketProvider');
  }
  return context;
};